import { motion } from "framer-motion";
import { ArrowUpRight, Mail } from "lucide-react";
import { useConsent } from "@/consent/ConsentContext";
import Reveal, { EASE } from "./Reveal";

export default function Finale({ onContact, onExplore, pedestalRef }) {
  const { openSettings } = useConsent();
  return (
    <section data-testid="finale" className="relative px-6 pb-10 pt-32 sm:pt-44">
      <div className="text-center">
        <Reveal>
          <p className="f-mono text-[11px] tracking-[0.4em] text-[#B8860B]">FINALE</p>
        </Reveal>
        <Reveal delay={0.1} className="mx-auto mt-10 w-fit">
          <img
            src="/logo-gold.png"
            alt="SYPAMA Holding GmbH"
            data-testid="finale-logo"
            className="mx-auto w-[min(72vw,540px)]"
          />
        </Reveal>
        <Reveal delay={0.3}>
          <p className="f-display mt-6 text-2xl font-light italic text-slate-700 sm:text-3xl">
            Capital with perspective.
          </p>
        </Reveal>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
          className="mt-14 flex flex-col items-center justify-center gap-4 sm:flex-row"
        >
          <button
            data-testid="explore-button"
            onClick={onExplore}
            className="f-mono group flex items-center gap-3 rounded-full border border-slate-300 px-8 py-4 text-[11px] tracking-[0.35em] text-slate-800 transition-colors duration-500 hover:border-[#D4AF37] hover:text-[#B8860B]"
          >
            EXPLORE SYPAMA
            <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
          <button
            data-testid="contact-button"
            onClick={onContact}
            className="f-mono group flex items-center gap-3 rounded-full bg-[#D4AF37] px-8 py-4 text-[11px] tracking-[0.35em] text-white transition-colors duration-500 hover:bg-[#B8860B]"
          >
            CONTACT
            <Mail className="h-4 w-4" />
          </button>
        </motion.div>
      </div>

      <div className="mt-32 flex flex-col items-center">
        <div ref={pedestalRef} data-testid="apple-pedestal" className="relative h-10 w-44">
          <div className="absolute inset-x-4 bottom-0 h-6 rounded-[50%] border border-[#D4AF37]/50" />
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          <div className="absolute inset-x-8 bottom-0 h-10 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.22),transparent_70%)]" />
        </div>

        <div
          data-testid="footer-info-box"
          className="mt-8 w-full max-w-2xl border border-[#D4AF37]/25 bg-white/75 px-8 py-9 text-center shadow-[0_20px_60px_rgba(212,175,55,0.10)] backdrop-blur-md"
        >
          <p className="f-mono mb-5 text-[10px] tracking-[0.35em] text-[#B8860B]">
            REAL ESTATE &middot; EQUITIES &middot; PRIVATE EQUITY
          </p>
          <p className="text-sm font-light leading-relaxed text-slate-600">
            SYPAMA Holding GmbH &middot; Turbinenstra&szlig;e 8 &middot; 70499 Stuttgart
          </p>
          <p className="mt-1 text-sm font-light text-slate-500">
            HRB 805645 &middot; Amtsgericht Stuttgart &middot; Managing Director: Pablo Penzel
          </p>
        </div>
      </div>

      <div className="mt-16 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-t border-slate-200 pt-6">
        <a
          href="#/impressum"
          data-testid="footer-impressum-link"
          className="f-mono text-[10px] tracking-[0.25em] text-slate-500 transition-colors hover:text-[#B8860B]"
        >
          IMPRESSUM
        </a>
        <a
          href="#/datenschutz"
          data-testid="footer-datenschutz-link"
          className="f-mono text-[10px] tracking-[0.25em] text-slate-500 transition-colors hover:text-[#B8860B]"
        >
          DATENSCHUTZ
        </a>
        <button
          data-testid="cookie-settings-link"
          onClick={openSettings}
          className="f-mono text-[10px] tracking-[0.25em] text-slate-500 transition-colors hover:text-[#B8860B]"
        >
          COOKIE-EINSTELLUNGEN
        </button>
        <span className="f-mono text-[10px] tracking-[0.25em] text-slate-400">
          &copy; 2026 SYPAMA HOLDING GMBH &middot; STUTTGART
        </span>
      </div>
    </section>
  );
}
